export interface PageLayoutField {
  id: string;
  label: string;
  type: string;
  pii: boolean;
  required: boolean;
  visible: boolean;
  width: number;
  encrypted?: boolean;
  encryptionLevel?: 'none' | 'standard' | 'high';
  autoPopulate?: boolean;
  accessRoles?: string[];
  masking?: {
    enabled: boolean;
    pattern: string;
    unmaskRoles: string[];
  };
}

export interface PageLayoutSection {
  id: string;
  name: string;
  columns: number;
  visible: boolean;
  collapsible: boolean;
  application: string;
  page?: string;
  tab?: string;
  order: number;
  fields: PageLayoutField[];
}

export interface PageLayout {
  id: string;
  name: string;
  objectType: string; // 'client', 'task', etc.
  applicationId: string; // 'regal-main', 'credit-management', 'cybersweep', 'global'
  sections: PageLayoutSection[];
  createdAt: string;
  updatedAt: string;
}

const STORAGE_KEY = 'pageLayouts';

// Default available fields that can be used across all page layouts
export const defaultAvailableFields: PageLayoutField[] = [
  {
    id: 'field-1', label: 'First Name', type: 'text', pii: true, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-2', label: 'Last Name', type: 'text', pii: true, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-3', label: 'Email', type: 'email', pii: true, required: false, visible: true, width: 50,
    encryptionLevel: 'standard', autoPopulate: true, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-4', label: 'Phone', type: 'phone', pii: true, required: false, visible: true, width: 50,
    encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
    masking: { enabled: true, pattern: '(XXX) XXX-1234', unmaskRoles: ['Admin'] }
  },
  {
    id: 'field-5', label: 'SSN', type: 'ssn', pii: true, encrypted: true, required: false, visible: true, width: 50,
    encryptionLevel: 'high', autoPopulate: false, accessRoles: ['Admin'],
    masking: { enabled: true, pattern: 'XXX-XX-1234', unmaskRoles: ['Admin'] }
  },
  {
    id: 'field-6', label: 'Date of Birth', type: 'date', pii: true, required: false, visible: true, width: 50,
    encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-7', label: 'City', type: 'text', pii: false, required: false, visible: true, width: 33,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-8', label: 'State', type: 'select', pii: false, required: false, visible: true, width: 33,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-9', label: 'ZIP Code', type: 'text', pii: false, required: false, visible: true, width: 33,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-10', label: 'Client ID', type: 'text', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-11', label: 'Credit Score', type: 'number', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-12', label: 'Dispute Status', type: 'select', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-13', label: 'Case Number', type: 'text', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-14', label: 'Address Line 1', type: 'text', pii: true, required: false, visible: true, width: 100,
    encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-15', label: 'PAW Status', type: 'select', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-16', label: 'Last Activity', type: 'datetime', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-17', label: 'Bureau', type: 'select', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-18', label: 'Account Status', type: 'select', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-19', label: 'Name', type: 'text', pii: true, required: false, visible: true, width: 100,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-20', label: 'Segment', type: 'select', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-21', label: 'Status', type: 'select', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-22', label: 'Owner', type: 'text', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-23', label: 'Risk Tier', type: 'select', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-24', label: 'Last Contact', type: 'date', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-25', label: 'Products', type: 'text', pii: false, required: false, visible: true, width: 100,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-26', label: 'Representative', type: 'text', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-27', label: 'Account Name', type: 'text', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-28', label: 'Account Owner', type: 'text', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-29', label: 'Spouse', type: 'text', pii: true, required: false, visible: true, width: 50,
    encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-30', label: 'Mobile Phone', type: 'phone', pii: true, required: false, visible: true, width: 50,
    encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
    masking: { enabled: true, pattern: '(XXX) XXX-1234', unmaskRoles: ['Admin'] }
  },
  {
    id: 'field-31', label: 'Primary Parent', type: 'text', pii: true, required: false, visible: true, width: 50,
    encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-32', label: 'Birthday', type: 'date', pii: true, required: false, visible: true, width: 50,
    encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-33', label: 'Team Email', type: 'email', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-34', label: 'Email CCs', type: 'text', pii: false, required: false, visible: true, width: 100,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-35', label: 'Estimated Net Worth', type: 'currency', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-36', label: 'Annual Salary', type: 'currency', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-37', label: 'Employer', type: 'text', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-38', label: 'Service Management', type: 'text', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-39', label: 'Account Manager', type: 'text', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-40', label: 'Account Manager Company', type: 'text', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
  {
    id: 'field-41', label: 'Day To Day Contact', type: 'text', pii: false, required: false, visible: true, width: 50,
    encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
    masking: { enabled: false, pattern: '', unmaskRoles: [] }
  },
];

// Default Client Page Layout for Global application
const defaultClientGlobalLayout: PageLayout = {
  id: 'page-layout-client-global',
  name: 'Client Page Layout (Global)',
  objectType: 'client',
  applicationId: 'global',
  sections: [
    {
      id: '1',
      name: 'Client Overview',
      columns: 2,
      visible: true,
      collapsible: true,
      application: 'global',
      order: 1,
      fields: [
        defaultAvailableFields[0], // First Name
        defaultAvailableFields[1], // Last Name
        defaultAvailableFields[9], // Client ID
        defaultAvailableFields[17], // Account Status
      ]
    },
    {
      id: '2',
      name: 'Contact Information',
      columns: 2,
      visible: true,
      collapsible: true,
      application: 'global',
      order: 2,
      fields: [
        defaultAvailableFields[2], // Email
        defaultAvailableFields[3], // Phone
        defaultAvailableFields[13], // Address Line 1
        defaultAvailableFields[6], // City
        defaultAvailableFields[7], // State
        defaultAvailableFields[8], // ZIP Code
      ]
    },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

// Default Client Page Layout for Credit Management
const defaultClientCreditLayout: PageLayout = {
  id: 'page-layout-client-credit-management',
  name: 'Client Page Layout (Credit Management)',
  objectType: 'client',
  applicationId: 'credit-management',
  sections: [
    {
      id: '3',
      name: 'Credit Details',
      columns: 2,
      visible: true,
      collapsible: true,
      application: 'credit-management',
      order: 1,
      fields: [
        defaultAvailableFields[10], // Credit Score
        defaultAvailableFields[16], // Bureau
        defaultAvailableFields[4], // SSN
        defaultAvailableFields[5], // Date of Birth
      ]
    },
    {
      id: '4',
      name: 'Dispute Information',
      columns: 1,
      visible: true,
      collapsible: true,
      application: 'credit-management',
      order: 2,
      fields: [
        defaultAvailableFields[12], // Case Number
        defaultAvailableFields[11], // Dispute Status
        defaultAvailableFields[15], // Last Activity
      ]
    },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

// Default Client Page Layout for CyberSweep
const defaultClientCyberSweepLayout: PageLayout = {
  id: 'page-layout-client-cybersweep',
  name: 'Client Page Layout (CyberSweep)',
  objectType: 'client',
  applicationId: 'cybersweep',
  sections: [
    {
      id: '5',
      name: 'CyberSweep Client Info',
      columns: 2,
      visible: true,
      collapsible: true,
      application: 'cybersweep',
      order: 1,
      fields: [
        defaultAvailableFields[0], // First Name
        defaultAvailableFields[1], // Last Name
        defaultAvailableFields[2], // Email
        defaultAvailableFields[3], // Phone
        defaultAvailableFields[4], // SSN
        defaultAvailableFields[5], // Date of Birth
      ]
    },
    {
      id: '6',
      name: 'PAW Records',
      columns: 1,
      visible: true,
      collapsible: true,
      application: 'cybersweep',
      order: 2,
      fields: [
        defaultAvailableFields[14], // PAW Status
        defaultAvailableFields[12], // Case Number
        defaultAvailableFields[15], // Last Activity
      ]
    },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

// Default Client Page Layout for Regal Main
const defaultClientRegalMainLayout: PageLayout = {
  id: 'page-layout-client-regal-main',
  name: 'Client Page Layout (Regal Main)',
  objectType: 'client',
  applicationId: 'regal-main',
  sections: [
    {
      id: '7',
      name: 'Client Information',
      columns: 2,
      visible: true,
      collapsible: true,
      application: 'regal-main',
      page: 'clients-record',
      tab: 'overview',
      order: 1,
      fields: [
        defaultAvailableFields[26], // Account Name
        defaultAvailableFields[27], // Account Owner
        defaultAvailableFields[28], // Spouse
        defaultAvailableFields[30], // Primary Parent
        defaultAvailableFields[31], // Birthday
        defaultAvailableFields[4], // SSN
      ]
    },
    {
      id: '8',
      name: 'Contact Information',
      columns: 2,
      visible: true,
      collapsible: true,
      application: 'regal-main',
      page: 'clients-record',
      tab: 'overview',
      order: 2,
      fields: [
        defaultAvailableFields[3], // Phone
        defaultAvailableFields[29], // Mobile Phone
        defaultAvailableFields[2], // Email
        defaultAvailableFields[32], // Team Email
        defaultAvailableFields[33], // Email CCs
      ]
    },
    {
      id: '9',
      name: 'Financial & Employment',
      columns: 2,
      visible: true,
      collapsible: true,
      application: 'regal-main',
      page: 'clients-record',
      tab: 'overview',
      order: 3,
      fields: [
        defaultAvailableFields[34], // Estimated Net Worth
        defaultAvailableFields[35], // Annual Salary
        defaultAvailableFields[36], // Employer
      ]
    },
    {
      id: '10',
      name: 'Account Management',
      columns: 2,
      visible: true,
      collapsible: true,
      application: 'regal-main',
      page: 'clients-record',
      tab: 'overview',
      order: 4,
      fields: [
        defaultAvailableFields[37], // Service Management
        defaultAvailableFields[38], // Account Manager
        defaultAvailableFields[39], // Account Manager Company
        defaultAvailableFields[40], // Day-to-Day Contact
        defaultAvailableFields[19], // Segment
        defaultAvailableFields[20], // Status
        defaultAvailableFields[22], // Risk Tier
        defaultAvailableFields[25], // Representative
      ]
    },
    // Tasks Tab - Configurable section (falls back to hardcoded if no fields)
    {
      id: '11',
      name: 'Client Tasks',
      columns: 2,
      visible: true,
      collapsible: true,
      application: 'regal-main',
      page: 'clients-record',
      tab: 'tasks',
      order: 1,
      fields: [
        // Add task-related fields here if needed
        // For now, empty fields will show the hardcoded task list
      ]
    },
    // Products Tab - Configurable section (falls back to hardcoded if no fields)
    {
      id: '12',
      name: 'Enrolled Products & Services',
      columns: 2,
      visible: true,
      collapsible: true,
      application: 'regal-main',
      page: 'clients-record',
      tab: 'products',
      order: 1,
      fields: [
        defaultAvailableFields[24], // Products
      ]
    },
    // Emails Tab - Configurable section (falls back to hardcoded if no fields)
    {
      id: '13',
      name: 'Email Communications',
      columns: 2,
      visible: true,
      collapsible: true,
      application: 'regal-main',
      page: 'clients-record',
      tab: 'emails',
      order: 1,
      fields: [
        defaultAvailableFields[2],  // Email
        defaultAvailableFields[32], // Team Email
        defaultAvailableFields[33], // Email CCs
      ]
    },
    // Notes Tab - Configurable section (falls back to hardcoded if no fields)
    {
      id: '14',
      name: 'Notes & Comments',
      columns: 1,
      visible: true,
      collapsible: true,
      application: 'regal-main',
      page: 'clients-record',
      tab: 'notes',
      order: 1,
      fields: [
        // Notes are managed through the notes modal
        // This section can be used for note metadata or quick notes
      ]
    },
    // Files Tab - Configurable section (falls back to hardcoded if no fields)
    {
      id: '15',
      name: 'Document Summary',
      columns: 1,
      visible: true,
      collapsible: true,
      application: 'regal-main',
      page: 'clients-record',
      tab: 'files',
      order: 1,
      fields: [
        // Files are managed through upload system
        // This section can show file metadata or categories
      ]
    },
    // History Tab - Configurable section (falls back to hardcoded if no fields)
    {
      id: '16',
      name: 'Recent Activity',
      columns: 1,
      visible: true,
      collapsible: true,
      application: 'regal-main',
      page: 'clients-record',
      tab: 'history',
      order: 1,
      fields: [
        defaultAvailableFields[15], // Last Activity
        defaultAvailableFields[23], // Last Contact
      ]
    },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

// Get all page layouts
export const getPageLayouts = (): Record<string, PageLayout> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading page layouts:', error);
  }

  // Return default layouts
  const defaultLayouts = {
    [defaultClientGlobalLayout.id]: defaultClientGlobalLayout,
    [defaultClientCreditLayout.id]: defaultClientCreditLayout,
    [defaultClientCyberSweepLayout.id]: defaultClientCyberSweepLayout,
    [defaultClientRegalMainLayout.id]: defaultClientRegalMainLayout,
  };

  savePageLayouts(defaultLayouts);
  return defaultLayouts;
};

// Get a specific page layout by ID
export const getPageLayout = (id: string): PageLayout | null => {
  const layouts = getPageLayouts();
  return layouts[id] || null;
};

// Get page layout by object type and application
export const getPageLayoutByType = (objectType: string, applicationId: string): PageLayout | null => {
  const layouts = getPageLayouts();
  const layout = Object.values(layouts).find(
    l => l.objectType === objectType && l.applicationId === applicationId
  );
  return layout || null;
};

// Save all page layouts
export const savePageLayouts = (layouts: Record<string, PageLayout>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layouts));
  } catch (error) {
    console.error('Error saving page layouts:', error);
  }
};

// Save a specific page layout
export const savePageLayout = (layout: PageLayout): void => {
  const layouts = getPageLayouts();
  layouts[layout.id] = {
    ...layout,
    updatedAt: new Date().toISOString()
  };
  savePageLayouts(layouts);
};

// Update sections for a page layout
export const updatePageLayoutSections = (id: string, sections: PageLayoutSection[]): void => {
  const layout = getPageLayout(id);
  if (layout) {
    layout.sections = sections;
    savePageLayout(layout);
  }
};

// Reset to default configuration
export const resetPageLayout = (id: string): void => {
  const defaultLayouts: Record<string, PageLayout> = {
    [defaultClientGlobalLayout.id]: defaultClientGlobalLayout,
    [defaultClientCreditLayout.id]: defaultClientCreditLayout,
    [defaultClientCyberSweepLayout.id]: defaultClientCyberSweepLayout,
    [defaultClientRegalMainLayout.id]: defaultClientRegalMainLayout,
  };

  if (defaultLayouts[id]) {
    savePageLayout({
      ...defaultLayouts[id],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  }
};

// Get all available fields (for use in admin panel)
export const getAvailableFields = (): PageLayoutField[] => {
  return defaultAvailableFields;
};

// Clear all layouts and reset to defaults (useful for testing or migrations)
export const clearAndResetAllLayouts = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('All page layouts cleared. Defaults will be reloaded on next access.');
  } catch (error) {
    console.error('Error clearing page layouts:', error);
  }
};

// Force reset to default layouts (completely overwrites existing)
export const forceResetToDefaults = (): Record<string, PageLayout> => {
  try {
    const defaultLayouts = {
      [defaultClientGlobalLayout.id]: defaultClientGlobalLayout,
      [defaultClientCreditLayout.id]: defaultClientCreditLayout,
      [defaultClientCyberSweepLayout.id]: defaultClientCyberSweepLayout,
      [defaultClientRegalMainLayout.id]: defaultClientRegalMainLayout,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultLayouts));
    console.log('Page layouts force reset to defaults:', defaultLayouts);

    // Return the layouts to verify
    return defaultLayouts;
  } catch (error) {
    console.error('Error force resetting page layouts:', error);
    throw error;
  }
};

// Add helper to call from browser console for testing
if (typeof window !== 'undefined') {
  (window as any).resetPageLayouts = clearAndResetAllLayouts;
  (window as any).forceResetPageLayouts = forceResetToDefaults;
}

export default {
  getPageLayouts,
  getPageLayout,
  getPageLayoutByType,
  savePageLayout,
  savePageLayouts,
  updatePageLayoutSections,
  resetPageLayout,
  getAvailableFields,
  clearAndResetAllLayouts,
  forceResetToDefaults,
  defaultAvailableFields,
};
